import { useEffect, useMemo, useRef, useState } from 'react'
import {
    useController,
    useFieldArray,
    useFormState,
    useWatch,
} from 'react-hook-form'
import { Plus, X } from 'lucide-react'
import { GrowerAreaMap } from './area-map/grower-area-map'
import { AreaCoordTable } from './area-map/area-coord-table'
import { normalizeGeo } from './area-map/use-area-polygon'
import { AreaCameraPanel } from './area-cameras/area-camera-panel'
import { AREA_VIEW_H, AreaViewToggle } from './area-cameras/area-view-toggle'
import { GrowerAttachmentsField } from './attachments/grower-attachments-field'
import { nextAreaDraftKey } from './grower-form-values'
import type { AreaLandDrafts } from './attachments/use-area-land-drafts'
import type { AreaView } from './area-cameras/area-view-toggle'
import type { AreaGeo } from './area-map/use-area-polygon'
import type { AreaMapItem } from '@/lib/api/grower-area-map-api'
import type { Control } from 'react-hook-form'
import type { GrowerFormValues } from '@/lib/schemas/grower-schemas'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useGrowerAreaCameras } from '@/hooks/use-cameras-query'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

type Props = {
    control: Control<GrowerFormValues>
    readOnly?: boolean
    setAreaGeo: (index: number, geo: AreaGeo) => void
    /**
     * Nháp giấy tờ đất theo từng vùng. Bỏ trống thì tab vùng không có ô đính
     * kèm — dùng cho màn chỉ hiển thị vùng, không quản giấy tờ.
     */
    landDrafts?: AreaLandDrafts
    /** Hộ đang sửa (undefined khi tạo mới) — để lớp nền bản đồ ẩn bản DB của hộ. */
    growerId?: string
}

/**
 * Section "Vùng trồng" dạng tab: mỗi tab là một vùng (bản đồ trái + thông tin
 * phải). Cho thêm/xoá vùng khi có quyền ghi.
 */
export function GrowerAreasSection({
    control,
    readOnly,
    setAreaGeo,
    landDrafts,
    growerId,
}: Props) {
    const { fields, append, remove } = useFieldArray({ control, name: 'areas' })
    const { errors, submitCount } = useFormState({ control })
    const [active, setActive] = useState(0)
    const idx = Math.min(active, Math.max(fields.length - 1, 0))

    // Chỉ vùng đang mở mới được render, nên lỗi của vùng ở tab khác không có
    // chỗ nào hiện ra: người dùng bấm Lưu, form chặn submit, màn hình đứng im.
    // Mỗi lần submit hỏng thì nhảy về đúng tab đang sai.
    const areaErrors = Array.isArray(errors.areas) ? errors.areas : []
    const lastHandledSubmit = useRef(0)
    useEffect(() => {
        if (submitCount === lastHandledSubmit.current) return
        lastHandledSubmit.current = submitCount
        const firstBad = areaErrors.findIndex(Boolean)
        if (firstBad >= 0) setActive(firstBad)
    }, [submitCount, areaErrors])

    const handleRemove = (i: number) => {
        // Vùng đã lưu mới hỏi lại: xoá nhầm là mất luôn toạ độ đã khoanh và mọi
        // nhật ký chăm sóc trỏ vào vùng đó. Vùng vừa thêm chưa có mã thì bỏ đi
        // không mất gì, hỏi chỉ tổ vướng.
        const saved = !!fields[i]?.code?.trim()
        if (saved && !window.confirm(m.grower_area_delete_confirm())) return
        // Quên nháp của vùng vừa bỏ đi: giữ lại thì lần Lưu kế tiếp vẫn tải
        // giấy tờ của một vùng không còn tồn tại lên server.
        const draftKey = fields[i]?.draftKey
        if (draftKey) landDrafts?.dropDraft(draftKey)
        remove(i)
        setActive(0)
    }

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
                {fields.map((f, i) => {
                    const hasError = !!areaErrors[i]
                    return (
                        <div
                            key={f.id}
                            data-testid={
                                hasError ? 'grower-tab-error' : undefined
                            }
                            className={twJoin(
                                'inline-flex h-9 items-center gap-1.5 rounded-md border pl-3.5 pr-1.5 text-sm font-semibold',
                                hasError &&
                                    'border-destructive text-destructive',
                                !hasError &&
                                    (i === idx
                                        ? 'border-primary bg-accent text-accent-foreground'
                                        : 'bg-card'),
                            )}
                        >
                            {hasError && (
                                <span
                                    aria-hidden
                                    className="size-1.5 rounded-full bg-destructive"
                                />
                            )}
                            <button
                                type="button"
                                onClick={() => setActive(i)}
                                className="py-2"
                            >
                                <TabName control={control} index={i} />
                            </button>
                            {!readOnly && (
                                <button
                                    type="button"
                                    title={m.grower_area_delete()}
                                    aria-label={m.grower_area_delete()}
                                    onClick={() => handleRemove(i)}
                                    className="flex size-5 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                >
                                    <X className="size-3.5" />
                                </button>
                            )}
                        </div>
                    )
                })}
                {!readOnly && (
                    <button
                        type="button"
                        data-testid="grower-add-area"
                        onClick={() => {
                            append({
                                // Khoá nháp cấp ngay tại đây để giấy tờ đất chọn
                                // trước khi bấm Lưu vẫn bám đúng vùng này.
                                draftKey: nextAreaDraftKey(),
                                // Không mint id ở client: server tự cấp UUID cho
                                // vùng thiếu id khi lưu, còn `crypto.randomUUID`
                                // chỉ tồn tại trong secure context — mở app qua
                                // HTTP trong mạng LAN là nút này ném TypeError.
                                // Mã "MVTNNN" cũng do backend cấp nên để trống.
                                code: '',
                                name: '',
                                acreage: '',
                                note: '',
                                paths: [],
                                markers: [],
                            })
                            setActive(fields.length)
                        }}
                        className="inline-flex h-9 items-center gap-2 rounded-md border border-dashed px-3.5 text-sm text-muted-foreground"
                    >
                        <Plus className="size-4" /> {m.grower_area_add()}
                    </button>
                )}
            </div>

            {fields.length > 0 && (
                <AreaPanel
                    control={control}
                    index={idx}
                    // Khoá fit bản đồ phải bám danh tính vùng, không bám index:
                    // xoá vùng đang xem thì index 0 vẫn là 0, bản đồ tưởng chưa
                    // đổi vùng và giữ nguyên khung nhìn của vùng vừa bị xoá.
                    fitKey={fields[idx]?.id}
                    draftKey={fields[idx]?.draftKey}
                    readOnly={readOnly}
                    setAreaGeo={setAreaGeo}
                    landDrafts={landDrafts}
                    growerId={growerId}
                />
            )}
        </div>
    )
}

/**
 * Nhãn tab = tên vùng "sống" theo đúng index (tránh lệch khi xoá vùng). Mã
 * "MVTNNN" do hệ thống cấp không nói lên vùng nào là vùng nào, nên tab hiển thị
 * tên do người dùng đặt; vùng mới chưa kịp nhập tên lùi về "Vùng N" để tab
 * không rỗng và vẫn bấm được.
 */
function TabName({
    control,
    index,
}: {
    control: Control<GrowerFormValues>
    index: number
}) {
    const name = useWatch({ control, name: `areas.${index}.name` })
    const text = typeof name === 'string' ? name.trim() : ''
    return <>{text || m.grower_area_tab_fallback({ n: index + 1 })}</>
}

/**
 * Thửa khác của CHÍNH hộ này lấy từ form — kể cả vùng vừa thêm chưa lưu và vùng
 * đã đổi toạ độ — để bản đồ của tab đang mở vẽ chúng làm nền (khầy Long: tạo
 * nhiều vùng chưa lưu vẫn phải thấy trên bản đồ, không chỉ vùng trong DB).
 * Khoá theo id vùng, không có thì draftKey — cùng cách khoá với giấy tờ đất.
 */
function useSiblingAreas(
    control: Control<GrowerFormValues>,
    activeIndex: number,
    growerId?: string,
): AreaMapItem[] {
    const areas = useWatch({ control, name: 'areas' })
    const growerName = useWatch({ control, name: 'name' })
    const nickname = useWatch({ control, name: 'nickname' })
    return useMemo(
        () =>
            (areas ?? []).flatMap((a, i) => {
                if (i === activeIndex) return []
                const paths = normalizeGeo(a.paths, a.markers).paths
                if (paths.length < 3) return []
                return [
                    {
                        area_id: a.id ?? a.draftKey ?? `idx-${i}`,
                        code: a.code ?? '',
                        name:
                            a.name?.trim() ||
                            m.grower_area_tab_fallback({ n: i + 1 }),
                        acreage: Number(a.acreage) || 0,
                        // Hộ đang tạo chưa có id: mọi thửa của nó cùng một
                        // khoá màu để vẫn "cùng hộ cùng màu".
                        grower_id: growerId ?? 'draft',
                        grower_name: growerName?.trim() || '',
                        grower_nickname: nickname?.trim() || null,
                        paths,
                    },
                ]
            }),
        [areas, activeIndex, growerId, growerName, nickname],
    )
}

function AreaPanel({
    control,
    index,
    fitKey,
    draftKey,
    readOnly,
    setAreaGeo,
    landDrafts,
    growerId,
}: {
    control: Control<GrowerFormValues>
    index: number
    fitKey?: string
    draftKey?: string
    readOnly?: boolean
    setAreaGeo: (index: number, geo: AreaGeo) => void
    landDrafts?: AreaLandDrafts
    growerId?: string
}) {
    const name = useController({ control, name: `areas.${index}.name` })
    const acreage = useController({ control, name: `areas.${index}.acreage` })
    const note = useController({ control, name: `areas.${index}.note` })
    const paths = useController({ control, name: `areas.${index}.paths` })
    const markers = useController({ control, name: `areas.${index}.markers` })
    const areaId = useWatch({ control, name: `areas.${index}.id` })
    const siblings = useSiblingAreas(control, index, growerId)

    // Vùng chưa lưu (chưa có id) thì chưa gắn được camera nên không gọi API.
    const { data: cameras = [] } = useGrowerAreaCameras(areaId)
    // Bản đồ và camera dùng chung khung bên trái: đổi qua lại không đẩy cột
    // thông tin bên phải nhảy chỗ. Giữ nguyên lựa chọn khi chuyển sang vùng
    // khác — đang soát camera cả vườn thì không phải bấm lại từng vùng.
    const [view, setView] = useState<AreaView>('map')

    const geo = normalizeGeo(paths.field.value, markers.field.value)
    const apply = (g: AreaGeo) => {
        paths.field.onChange(g.paths)
        markers.field.onChange(g.markers)
        setAreaGeo(index, g)
    }

    return (
        <div className="grid gap-5 md:grid-cols-[1.5fr_1fr]">
            <div className="space-y-2.5">
                {/* Vùng chưa lưu chưa có id nên không thể gắn camera: hiện nút
                    "Camera (0)" chỉ tổ đánh lừa người đang tạo hộ mới. */}
                {areaId && (
                    <AreaViewToggle
                        view={view}
                        onChange={setView}
                        cameraCount={cameras.length}
                    />
                )}
                {!areaId || view === 'map' ? (
                    <GrowerAreaMap
                        value={geo}
                        onChange={apply}
                        readOnly={readOnly}
                        fitKey={fitKey}
                        neighbors={{
                            growerId,
                            siblings,
                            excludeAreaIds: areaId ? [areaId] : [],
                        }}
                    />
                ) : (
                    <AreaCameraPanel
                        areaId={areaId}
                        height={AREA_VIEW_H}
                        canWrite={!readOnly}
                    />
                )}
            </div>
            <div className="space-y-3.5">
                {/* Không có ô nhập mã vùng — mã do hệ thống tự tạo (MVTNNN) và
                    không hiển thị trong form; tab nhận diện vùng bằng tên. */}
                <Field
                    label={m.grower_area_name()}
                    required
                    error={name.fieldState.error?.message}
                >
                    <Input
                        data-testid="grower-area-name"
                        disabled={readOnly}
                        value={name.field.value ?? ''}
                        onChange={name.field.onChange}
                    />
                </Field>
                <Field
                    label={m.grower_area_acreage()}
                    required
                    error={acreage.fieldState.error?.message}
                >
                    <div className="relative">
                        <Input
                            data-testid="grower-area-acreage"
                            disabled={readOnly}
                            type="number"
                            step="0.01"
                            min="0"
                            className="pr-10"
                            value={acreage.field.value ?? ''}
                            onChange={acreage.field.onChange}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                            m²
                        </span>
                    </div>
                </Field>
                <Field label={m.grower_area_note()}>
                    <Textarea
                        disabled={readOnly}
                        value={note.field.value ?? ''}
                        onChange={note.field.onChange}
                    />
                </Field>
                <div>
                    <Label className="mb-1.5 block">
                        {m.grower_coordinates()}
                    </Label>
                    <AreaCoordTable
                        geo={geo}
                        onChange={apply}
                        readOnly={readOnly}
                    />
                </div>
                {/* Giấy tờ đất thuộc TỪNG vùng, không dùng chung cho cả hộ:
                    hộ ba vùng thì ba bìa đỏ khác nhau, gộp một chỗ là không
                    biết tờ nào của vùng nào. Vùng chưa lưu vẫn chọn được tệp —
                    chúng nằm chờ và lên server ngay sau khi bấm Lưu. */}
                {landDrafts && draftKey && (
                    <div className="space-y-1.5">
                        <Label>{m.grower_land_documents()}</Label>
                        <GrowerAttachmentsField
                            draft={landDrafts.getDraft(draftKey, areaId)}
                            readOnly={readOnly}
                            typesHint={m.grower_land_documents_hint()}
                            label={m.grower_land_documents()}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

function Field({
    label,
    required,
    error,
    children,
}: {
    label: string
    required?: boolean
    error?: string
    children: React.ReactNode
}) {
    return (
        <div className="space-y-1.5">
            <Label>
                {label}
                {required && <span className="text-destructive"> *</span>}
            </Label>
            {children}
            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    )
}
